/* ═══════════════════════════════════════════════════════════════
   Forma zone drag — the SLOT hit-test (pure, no DOM).

   Hand-built, approved by Josh 2026-10-27 (alternative: dnd-kit — rejected,
   the sheet's tokens are laid out by the Word-fidelity brain, not a list).

   A drag over a zone column does not pick a token, it picks a SLOT:
     - the LINE the pointer is on (the nearest one when it is between lines),
     - the GAP on that line whose midpoint is nearest the pointer
       (before the first token, between any two, after the last),
     - or, only at a token's centre, REPLACE that token,
     - or, over the thin bands above the first / below the last line, a
       deliberate NEW LINE.
   The caller measures the rendered boxes once per drag frame and feeds them
   in; nothing here touches the DOM or re-lays anything out.
   ═══════════════════════════════════════════════════════════════ */

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export type SlotKind = 'slot' | 'newline-above' | 'newline-below' | 'replace';

/** `index` is the gap number on the line (0..n) for `slot`/`newline-*`, or
 *  the token number (0..n-1) for `replace`. */
export interface Slot {
  line: number;
  index: number;
  kind: SlotKind;
}

export interface SlotOptions {
  /** Allow `replace` (the caller withholds it until the pointer has rested). */
  replace?: boolean;
  /** The thin "New line" bands (above the first / below the last line). */
  newLine?: { above?: Box | null; below?: Box | null };
}

/** The middle of a token that means "replace" — 40% wide, 50% tall. */
const REPLACE_X = 0.3;
const REPLACE_Y = 0.25;

const inside = (b: Box, x: number, y: number) =>
  x >= b.left && x <= b.right && y >= b.top && y <= b.bottom;

/** The x of gap `i` on a line of token boxes (a gap sits on the edge of the
 *  first/last token, and at the midpoint of the space between two). */
export function gapX(tokens: Box[], i: number): number {
  if (tokens.length === 0) return 0;
  if (i <= 0) return tokens[0].left;
  if (i >= tokens.length) return tokens[tokens.length - 1].right;
  return (tokens[i - 1].right + tokens[i].left) / 2;
}

function lineDistance(b: Box, y: number): number {
  if (y < b.top) return b.top - y;
  if (y > b.bottom) return y - b.bottom;
  return 0;
}

/**
 * Which slot is the pointer on?
 *
 * @param lineBoxes  one box per line, top to bottom
 * @param tokenBoxes per line, that line's token boxes, left to right
 * @param prev       the slot returned for the previous frame (hysteresis)
 * @param hysteresisPx how much better a new candidate must be before the
 *                   slot is allowed to change — keeps the gap from flickering
 *                   between two gaps (or two lines) at the dead zone.
 */
export function slotForPointer(
  lineBoxes: Box[],
  tokenBoxes: Box[][],
  x: number,
  y: number,
  prev: Slot | null,
  hysteresisPx: number,
  opts: SlotOptions = {}
): Slot | null {
  const n = lineBoxes.length;
  if (n === 0) return null;

  // 1. The deliberate new-line bands win — they are separate, visible things.
  const { above, below } = opts.newLine ?? {};
  if (above && inside(above, x, y)) return { line: 0, index: 0, kind: 'newline-above' };
  if (below && inside(below, x, y)) {
    return { line: n - 1, index: tokenBoxes[n - 1]?.length ?? 0, kind: 'newline-below' };
  }

  // 2. The line: the one the pointer is on; the nearest when between lines,
  //    and the previous one keeps it until another is clearly nearer.
  let line = 0;
  let best = Infinity;
  for (let i = 0; i < n; i++) {
    const d = lineDistance(lineBoxes[i], y);
    if (d < best) {
      best = d;
      line = i;
    }
  }
  if (best > 0 && prev && prev.line < n && prev.line !== line) {
    if (lineDistance(lineBoxes[prev.line], y) <= best + hysteresisPx) line = prev.line;
  }

  const toks = tokenBoxes[line] ?? [];
  if (toks.length === 0) return { line, index: 0, kind: 'slot' };

  // 3. Replace: only the token's centre, and only when the caller allows it.
  if (opts.replace !== false) {
    for (let i = 0; i < toks.length; i++) {
      const t = toks[i];
      const w = t.right - t.left;
      const h = t.bottom - t.top;
      const centre: Box = {
        left: t.left + w * REPLACE_X,
        right: t.right - w * REPLACE_X,
        top: t.top + h * REPLACE_Y,
        bottom: t.bottom - h * REPLACE_Y,
      };
      // Centre only — no sticky "stay on Replace": at a token's edge the gap wins,
      // so the ring is never shown (or fired) where the architect aimed at a gap.
      if (inside(centre, x, y)) return { line, index: i, kind: 'replace' };
    }
  }

  // 4. The gap whose midpoint is nearest horizontally.
  let gap = 0;
  let gd = Infinity;
  for (let i = 0; i <= toks.length; i++) {
    const d = Math.abs(x - gapX(toks, i));
    if (d < gd) {
      gd = d;
      gap = i;
    }
  }
  if (prev && prev.kind === 'slot' && prev.line === line && prev.index <= toks.length) {
    if (prev.index !== gap && Math.abs(x - gapX(toks, prev.index)) <= gd + hysteresisPx) {
      gap = prev.index;
    }
  }
  return { line, index: gap, kind: 'slot' };
}

/** Where a slot lands in the zone's RAW array. `gapRaw[i]` is the raw index
 *  of gap i on a line; `tokRaw[i]` the raw index of token i. */
export function rawIndexForSlot(slot: Slot, gapRaw: number[][], tokRaw: number[][]): number {
  if (slot.kind === 'replace') return tokRaw[slot.line]?.[slot.index] ?? 0;
  return gapRaw[slot.line]?.[slot.index] ?? 0;
}

/**
 * Raw gap indices for one line of rendered fragments. A wrapped literal paints
 * as several fragments under ONE raw index: a gap can only sit before its first
 * fragment or after its last, never inside it.
 */
export function gapRawForLine(
  raws: number[],
  frags: Array<'only' | 'first' | 'mid' | 'last'>
): number[] {
  const out: number[] = [];
  const n = raws.length;
  for (let i = 0; i < n; i++) {
    const startsHere = frags[i] === 'only' || frags[i] === 'first';
    out.push(startsHere ? raws[i] : raws[i] + 1);
  }
  if (n > 0) {
    const endsHere = frags[n - 1] === 'only' || frags[n - 1] === 'last';
    out.push(endsHere ? raws[n - 1] + 1 : raws[n - 1]);
  } else {
    out.push(0);
  }
  return out;
}

/** Rest tracking for Replace. A rest starts when the pointer enters a token's
 *  centre and RESTARTS whenever it travels more than `slackPx` from where the
 *  rest began — so a pass-through, or a drag still moving inside the centre,
 *  never arms. `armed` is true only after `restMs` of (near) stillness. */
export interface RestState {
  key: string;
  since: number;
  x: number;
  y: number;
}
export function stepRest(
  rest: RestState | null,
  key: string | null,
  x: number,
  y: number,
  now: number,
  restMs: number,
  slackPx: number
): { rest: RestState | null; armed: boolean } {
  if (key == null) return { rest: null, armed: false };
  if (!rest || rest.key !== key || Math.hypot(x - rest.x, y - rest.y) > slackPx) {
    return { rest: { key, since: now, x, y }, armed: false };
  }
  return { rest, armed: now - rest.since >= restMs };
}

/** Which column owns the pointer: a column whose rendered tokens contain it wins
 *  over the geometric third (a wide left column never reads as Center). */
export function columnByTokens(
  zones: Array<{ zoneKey: string; box: Box }>,
  x: number,
  y: number
): string | null {
  for (const z of zones) if (inside(z.box, x, y)) return z.zoneKey;
  return null;
}

/** The open-slot width: how far past its last token a column still owns the line. */
export const DEFAULT_GAP = 48;

export interface ColumnLines {
  zoneKey: string;
  /** Rendered token boxes per line id (the paper's row index), un-shifted. */
  lines: Map<number, Box[]>;
}

/** Which column owns the pointer. A column owns (1) every rendered token box,
 *  (2) on each of its lines, from its first token's left to its last token's
 *  right PLUS one gap, and (3) the trailing area up to the next column's first
 *  token on that line (abutting columns split at the midpoint). Only outside
 *  every owned span does the caller fall back to the geometric thirds. */
export function columnOwning(
  cols: ColumnLines[],
  x: number,
  y: number,
  gap: number = DEFAULT_GAP
): string | null {
  const ids = new Set<number>();
  cols.forEach((c) => c.lines.forEach((_, id) => ids.add(id)));
  for (const id of ids) {
    const ents: Array<{ key: string; L: number; R: number; toks: Box[] }> = [];
    let top = Infinity;
    let bottom = -Infinity;
    for (const c of cols) {
      const toks = c.lines.get(id);
      if (!toks || toks.length === 0) continue;
      ents.push({
        key: c.zoneKey,
        L: Math.min(...toks.map((b) => b.left)),
        R: Math.max(...toks.map((b) => b.right)),
        toks,
      });
      for (const b of toks) {
        top = Math.min(top, b.top);
        bottom = Math.max(bottom, b.bottom);
      }
    }
    if (y < top || y > bottom) continue;
    for (const e of ents) if (e.toks.some((b) => inside(b, x, y))) return e.key;
    ents.sort((a, b) => a.L - b.L);
    for (let i = 0; i < ents.length; i++) {
      const e = ents[i];
      const prev = i > 0 && ents[i - 1].R <= e.L ? ents[i - 1] : null;
      const next = i < ents.length - 1 && ents[i + 1].L >= e.R ? ents[i + 1] : null;
      const lo = prev ? (prev.R + e.L) / 2 : e.L;
      const hi = next ? (e.R + next.L) / 2 : e.R + gap;
      if (x >= lo && x <= hi) return e.key;
    }
    return null;
  }
  return null;
}

/** What a release drops on: the target that was SHOWN (rested Replace,
 *  hysteresis held), falling back to a fresh evaluation only when nothing was
 *  shown. What you see is exactly what you get. */
export function pickReleaseTarget<T>(shown: T | null, fresh: () => T | null): T | null {
  return shown ?? fresh();
}
